import { useEffect } from 'react';
import { Outlet, useLocation } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { TopBar } from './TopBar.js';
import { Sidebar } from './Sidebar.js';
import { MobileBottomNav } from './MobileBottomNav.js';
import { HeroBgVideo } from './HeroBgVideo.js';
import { CommandPalette } from '@/components/lobby/CommandPalette.js';
import { LobbyPopups } from '@/features/popups/LobbyPopups.js';
import { useAuthStore } from '@/stores/auth.js';
import { usePrefsStore } from '@/stores/prefs.js';
import { useUiStore } from '@/stores/ui.js';
import { useBrandingStore } from '@/stores/branding.js';
import { fetchWalletBalances, WALLET_QUERY_KEYS } from '@/features/wallet/api.js';
import { fetchMemberMe, MEMBER_QUERY_KEYS } from '@/features/member/api.js';
import { useDisplayRate } from '@/features/finance/api.js';
import { shellMoney } from '@/lib/money/display-rate.js';
import { useWalletStream } from '@/features/wallet/useWalletStream.js';
import { MoneyProvider } from '@/components/money/MoneyProvider.js';
import { GamePromoLockProvider } from '@/features/promo/game-lock-provider.js';
import { ImpersonationBanner } from '@/components/ImpersonationBanner.js';
import { useAutoLogout } from '@/features/auth/useAutoLogout.js';
import { useChatwootSync } from '@/lib/chatwoot/useChatwootSync.js';
import { usePresenceHeartbeat } from '@/features/presence/usePresenceHeartbeat.js';
import { SUPPORTED_LOCALES, type Locale } from '@/lib/i18n/index.js';

export function AppShell() {
  const location = useLocation();
  const currentPath = location.pathname;
  const subject = useAuthStore((s) => s.subject);
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthed = Boolean(accessToken);
  const searchOpen = useUiStore((s) => s.searchOpen);
  const setSearchOpen = useUiStore((s) => s.setSearchOpen);

  // Auth-only side effects — SSE wallet stream + token-refresh-on-focus.
  // The hooks no-op internally when there's no token, but we still skip
  // wiring them up to keep the unauth render path clean.
  useWalletStream();
  useAutoLogout();
  useChatwootSync();
  usePresenceHeartbeat();

  const walletQuery = useQuery({
    queryKey: WALLET_QUERY_KEYS.balances,
    queryFn: fetchWalletBalances,
    enabled: isAuthed,
    // SSE keeps the cache fresh. Poll every 30 min as a safety net only.
    staleTime: 30 * 60_000,
    refetchInterval: 30 * 60_000,
    refetchOnWindowFocus: false,
  });

  const memberQuery = useQuery({
    queryKey: MEMBER_QUERY_KEYS.me,
    queryFn: fetchMemberMe,
    enabled: isAuthed,
    staleTime: 30_000,
  });

  // Cross-device locale sync: when /members/me reports a preferred language
  // that differs from the locally chosen one, switch the UI to it. This is
  // best-effort — if the saved value isn't a supported locale, ignore it.
  const setLocale = usePrefsStore((s) => s.setLocale);
  const currentLocale = usePrefsStore((s) => s.locale);
  const memberPreferred = memberQuery.data?.member?.preferredLanguage ?? null;
  useEffect(() => {
    if (!memberPreferred) return;
    if (memberPreferred === currentLocale) return;
    if (!(SUPPORTED_LOCALES as readonly string[]).includes(memberPreferred)) return;
    setLocale(memberPreferred as Locale);
  }, [memberPreferred, currentLocale, setLocale]);

  // /site-config, fetched at boot — correct before login.
  const siteCurrency = useBrandingStore((s) => s.branding?.currency);
  const siteLedgerCurrency = useBrandingStore((s) => s.branding?.ledgerCurrency);
  const displayRate = useDisplayRate().data;

  const wallet = walletQuery.data;
  const username =
    memberQuery.data?.member?.username ?? subject?.displayName ?? undefined;

  const walletSnap = wallet
    ? {
        memberId: wallet.memberId,
        creditCash: wallet.creditCash,
        creditPromotion: wallet.creditPromotion,
        commissionWallet: wallet.commissionWallet,
        cashbackWallet: wallet.cashbackWallet,
        balance: wallet.balance,
        withdrawable: wallet.withdrawable,
        currency: wallet.currency,
        updatedAt: wallet.updatedAt,
      }
    : undefined;

  const money = shellMoney({ siteCurrency, displayRate, walletCurrency: walletSnap?.currency, siteLedgerCurrency });

  return (
    <MoneyProvider currency={money.currency} displayCurrency={money.displayCurrency} rate={money.rate}>
      {/* One promotion-lock subscriber for the whole shell — every tile below
          reads its verdict from here instead of opening its own (#524). */}
      <GamePromoLockProvider>
        {/* Immersive background — the Bifröst hero illustration shows only on the
            lobby; inner pages keep the plain deep-space canvas + starfield so
            their content stays clean and uncovered. */}
        {currentPath === '/' && <div className="bf-page-black" aria-hidden />}
        {currentPath === '/' && (
          <div className="bf-hero-bg" aria-hidden>
            <HeroBgVideo />
          </div>
        )}
        <div className="bf-stars" aria-hidden />
        <div className="bf-stars bf-stars-2" aria-hidden />

        <div className="flex min-h-dvh flex-col">
          {/* Read-only impersonation banner — self-gates on the per-tab flag, so it
              only renders during a staff "view as member" session. */}
          <ImpersonationBanner />
          {/* Full-width top navbar (Duel-style) spanning above everything */}
          <TopBar
            wallet={walletSnap}
            username={username}
            currentPath={currentPath}
          />

          {/* Left rail sits BELOW the navbar, alongside the content column */}
          <div className="flex flex-1 min-h-0">
            <Sidebar currentPath={currentPath} />

            <div className="flex-1 min-w-0 flex flex-col">
              {/* Breathing room below the sticky navbar on every page EXCEPT the
                  lobby, whose hero is meant to bleed up under the translucent bar
                  over the Bifröst bridge. Without this, inner-page content jams
                  flush against the navbar (top:0 gap) and reads as "covered". */}
              <main
                className={[
                  'flex-1 w-full mx-auto max-w-[1280px] px-4 md:px-6 pb-32 md:pb-10 flex flex-col gap-6',
                  currentPath === '/' ? '' : 'pt-6 md:pt-8',
                ].join(' ')}
              >
                <Outlet />
              </main>

              <MobileBottomNav currentPath={currentPath} />
            </div>
          </div>

          <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
          <LobbyPopups />
        </div>
      </GamePromoLockProvider>
    </MoneyProvider>
  );
}
